'use client';

import { useCallback, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { TextField } from '@picsart/design-system/components/TextField';
import { IconCross } from '@picsart/design-system/foundation/icons/IconCross';
import { IconSearch } from '@picsart/design-system/foundation/icons/IconSearch';
import { useClickOutsideRef } from '@picsart/design-system/hooks/dom';

import useSearch from '@/hooks/useSearch';
import type { SearchResultItem } from '@/types/search';
import SearchEmpty from '@/components/SearchEmpty';
import SearchRecent from '@/components/SearchRecent';
import SearchResults from '@/components/SearchResults';
import { SEARCH_TEST_IDS } from '@/constants/searchConstants/testIds';

import useStyles from './styles';

/**
 * Search in the site header: a field that suggests presets as you type, in a
 * popover anchored under it. Ported from `genai-studio-v2`'s `SearchDropdown`,
 * against the same endpoint — a term typed here and inside the miniapp returns
 * the same hits.
 *
 * Clicking a result opens that preset's page; Enter opens `/search`, which is
 * this site's addition (the miniapp has no results page — Enter there only
 * skips the debounce).
 *
 * The miniapp's `SearchBody` state switch is inlined below rather than ported:
 * it exists there so a mobile screen and a desktop popover can't drift, and
 * this repo has one surface.
 */
const SearchDropdown = () => {
  const classes = useStyles();
  // Dismissing hides the popover but keeps the term, so clicking away and back
  // doesn't cost the user what they typed.
  const [isDismissed, setIsDismissed] = useState(true);
  const {
    value,
    query,
    items,
    recentSearches,
    restrictionMessage,
    isFetching,
    isError,
    hasNoResults,
    handleChange: handleSearchChange,
    handleSubmit,
    handleClear,
    handleResultClick: openResult,
    handleRecentSelect,
    handleRecentRemove,
    handleRecentClearAll,
    handleRetry,
  } = useSearch();

  const dismiss = useCallback(() => setIsDismissed(true), []);

  /**
   * Opening a result navigates; the popover has to go with it.
   *
   * The miniapp deliberately leaves its search open — a result there opens a
   * feed *over* the search, which comes back when the feed closes. Here the
   * header survives the navigation, so an undismissed popover would still be
   * hanging over the preset page the user just landed on. The term is kept, so
   * coming back and focusing the field resumes where they left off.
   */
  const handleResultClick = useCallback(
    (item: SearchResultItem) => {
      dismiss();
      openResult(item);
    },
    [dismiss, openResult],
  );

  // `false` = listen on mousedown, not click. On click, the press that focuses
  // the field would still be propagating when the listener registers and would
  // immediately count as an outside click.
  const containerRef = useClickOutsideRef(dismiss, [], false);

  const handleChange = (nextValue: string) => {
    setIsDismissed(false);
    handleSearchChange(nextValue);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') dismiss();
  };

  // Open on focus even with an empty field — that's where recent searches live.
  // With no query and no history there is nothing to show, so the popover stays
  // collapsed for a first-time user.
  const hasPopoverContent = Boolean(query) || recentSearches.length > 0;

  const renderBody = () => {
    // Idle — nothing searched yet. Offer the user's own history rather than a
    // blank panel; `SearchRecent` renders nothing when there is no history.
    if (!query) {
      return <SearchRecent terms={recentSearches} onSelect={handleRecentSelect} onRemove={handleRecentRemove} onClearAll={handleRecentClearAll} />;
    }

    // Loading shows the results in skeleton mode, so the placeholders match the
    // real layout instead of a centred spinner that the results then displace.
    if (isFetching) return <SearchResults items={items} isLoading onItemClick={handleResultClick} />;

    // The backend's own moderation refused the term — a message to show, not an
    // error to retry.
    if (restrictionMessage) return <p className={classes.message}>{restrictionMessage}</p>;

    if (isError) {
      return <SearchEmpty title="Something went wrong" description="Check your connection and try again." isCompact onRetry={handleRetry} />;
    }

    if (hasNoResults) {
      return <SearchEmpty title="Sorry, we can’t find what you’re looking for." description="Please try something else." isCompact />;
    }

    return <SearchResults items={items} isLoading={false} onItemClick={handleResultClick} />;
  };

  return (
    <div ref={containerRef} className={classes.root} data-testid={SEARCH_TEST_IDS.INPUT_CONTAINER}>
      <TextField
        // Not `type="search"` — WebKit adds its own clear button on top of the
        // DS one.
        type="text"
        className={classes.field}
        value={value}
        placeholder="Search Gen AI Studio"
        startIcon={IconSearch}
        // DS renders the end icon as a clear button, and only once filled.
        endIcon={IconCross}
        iconClick={handleClear}
        aria-label="Search Gen AI Studio"
        dataTestId={SEARCH_TEST_IDS.INPUT}
        onChange={e => handleChange(e.target.value)}
        onFocus={() => setIsDismissed(false)}
        // Live results already track typing; Enter opens the full results page.
        onEnter={handleSubmit}
        onKeyDown={handleKeyDown}
      />
      {!isDismissed && hasPopoverContent && (
        <div className={classes.popover} data-testid={SEARCH_TEST_IDS.SUGGESTIONS_DROPDOWN}>
          {renderBody()}
        </div>
      )}
    </div>
  );
};

export default SearchDropdown;
